<template>
  <div class="fixed inset-0 bg-black/80 flex justify-center items-center z-50">
    <div class="bg-surface-0 dark:bg-surface-900 rounded-xl p-6 w-[90%] max-w-[800px] max-h-[90vh] overflow-y-auto text-surface-700 dark:text-surface-0 border border-surface-200 dark:border-surface-700">
      <div class="flex justify-between items-center mb-6 pb-4 border-b border-surface-200 dark:border-surface-700">
        <h2 class="m-0 text-2xl">{{ props.isViewMode ? '고장신고 상세' : '고장신고' }}</h2>
        <Button icon="pi pi-times" class="p-button-text p-button-rounded p-button-icon-only" @click="route.path.includes('/IssueReportDialog') ? router.back() : emit('close')" />
      </div>
      <Toast />
      <!-- 고장신고 내용 -->
      <div class="gap-6 mb-6">
        <div class="flex-1 min-w-0 flex flex-col gap-4" v-if="!props.isViewMode">
          <QrScanner @scanned="handleQRCode" />
        </div>

        <div class="flex-1 min-w-0 flex flex-col gap-2 mt-4">
          <label class="font-bold">자산 정보</label>
          <div class="p-5 bg-surface-100 dark:bg-surface-800 rounded-lg min-h-[150px] flex flex-col">
            <template v-if="assetInfo">
              <div class="mb-3">
                <span class="font-bold mr-2.5">자산번호:</span>
                <span>{{ assetInfo.assetNumber }}</span>
              </div>
              <div class="mb-3">
                <span class="font-bold mr-2.5">자산명:</span>
                <span>{{ assetInfo.assetName }}</span>
              </div>
              <div class="mb-3">
                <span class="font-bold mr-2.5">위치:</span>
                <span>{{ assetInfo.address }}</span>
              </div>
              <div class="mb-3">
                <span class="font-bold mr-2.5">상태:</span>
                <Tag :value="getStatusLabel(assetInfo.status)" :severity="getAssetStatusSeverity(assetInfo.status)" />
              </div>
              <div v-if="false" class="mb-3">
                <span class="font-bold mr-2.5">세부사항:</span>
                <span>{{ assetInfo.assetExplain }}</span>
              </div>
            </template>
            <div v-else class="text-surface-0/70 italic">QR 코드를 스캔하여 자산 정보를 확인하세요.</div>
          </div>

          <form class="flex flex-col gap-5" @submit.prevent="submitReport">
            <div class="flex flex-col gap-2 mt-4">
              <label class="font-bold">고장 유형 <span class="text-red-500 ml-1">*</span></label>
              
              <!-- 편집 모드: 고장 유형 선택 UI -->
              <div class="flex flex-col gap-2 mt-2" v-if="!props.isViewMode || isEditReportMode">
                <div v-for="(type, key) in issueTypes" 
                     :key="key" 
                     class="border border-surface-200 dark:border-surface-700 rounded-lg overflow-hidden transition-all duration-300"
                     :class="{ 'border-primary-500 shadow-[0_0_0_1px_#ff9800]': expandedGroups.has(key) }">
                  <div class="flex justify-between items-center p-3 bg-surface-100 dark:bg-surface-800 cursor-pointer transition-all duration-200 hover:bg-surface-200 dark:hover:bg-surface-700 select-none"
                       @click="toggleGroup(key)">
                    <div class="font-medium text-base">
                      {{ type.label }}
                    </div>
                    <span class="text-primary-500 text-sm transition-transform duration-300 flex items-center justify-center w-6 h-6 rounded"
                          :class="{ 'bg-primary-500/10': expandedGroups.has(key), '-rotate-90': !expandedGroups.has(key) }">
                      {{ expandedGroups.has(key) ? '▼' : '▶' }}
                    </span>
                  </div>
                  
                  <div class="p-2.5 px-4 bg-surface-50 dark:bg-surface-900 flex flex-col gap-0.5 transition-all duration-300"
                       v-show="expandedGroups.has(key)">
                    <div v-for="subType in type.subTypes" 
                         :key="subType.value" 
                         class="flex items-center gap-1.5 p-0.5 px-2 rounded transition-colors duration-200 hover:bg-surface-100 dark:hover:bg-surface-800">
                      <Checkbox :inputId="'subtype-' + subType.value" 
                               :value="`${key}_${subType.value}`" 
                               v-model="formData.issueSubType" />
                      <label :for="'subtype-' + subType.value" class="cursor-pointer text-base select-none">
                        {{ subType.label }}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- 읽기 모드: 선택된 고장 유형 표시 -->
              <div v-if="props.isViewMode && !isEditReportMode" class="mt-2">
                <div class="p-4 bg-surface-50 dark:bg-surface-900 rounded-lg border border-surface-200 dark:border-surface-700">
                  <h4 class="m-0 mb-3 text-base">신고된 고장 유형</h4>
                  <div v-if="formData.issueSubType.length > 0" class="flex flex-col gap-3">
                    <div v-for="type in selectedTypes" 
                         :key="type" 
                         class="bg-surface-100 dark:bg-surface-800 rounded-md overflow-hidden">
                      <div class="flex justify-between items-center p-2.5 px-3 bg-surface-200 dark:bg-surface-700">
                        <span class="font-medium">{{ issueTypes[type].label }}</span>
                      </div>
                      <div class="p-2 px-3 flex flex-wrap gap-2">
                        <div v-for="subType in getSelectedSubTypes(type)" 
                             :key="subType.value" 
                             class="px-2 py-1 bg-surface-50 dark:bg-surface-900 rounded text-sm text-surface-700 dark:text-surface-0/90">
                          <span>{{ subType.label }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-surface-500 dark:text-surface-400 italic">
                    고장 유형 정보가 없습니다.
                  </div>
                </div>
              </div>
            </div>

            <!-- 편집 모드에서만 표시되는 선택한 고장 유형 요약 -->
            <div class="my-5 p-4 bg-surface-50 dark:bg-surface-900 rounded-lg border border-surface-200 dark:border-surface-700" 
                 v-if="(!props.isViewMode || isEditReportMode) && formData.issueSubType.length > 0">
              <h4 class="m-0 mb-3 text-base">선택한 고장 유형</h4>
              <div class="flex flex-col gap-3">
                <div v-for="type in selectedTypes" 
                     :key="type" 
                     class="bg-surface-100 dark:bg-surface-800 rounded-md overflow-hidden">
                  <div class="flex justify-between items-center p-2.5 px-3 bg-surface-200 dark:bg-surface-700">
                    <span class="font-medium">{{ issueTypes[type].label }}</span>
                  </div>
                  <div class="p-2 px-3 flex flex-wrap gap-2">
                    <div v-for="subType in getSelectedSubTypes(type)" 
                         :key="subType.value" 
                         class="flex items-center gap-1.5 px-2 py-1 bg-surface-50 dark:bg-surface-900 rounded text-sm text-surface-700 dark:text-surface-0/90">
                      <span>{{ subType.label }}</span>
                      <Button icon="pi pi-times" class="p-button-text p-button-rounded p-button-icon-only p-button-sm text-red-500" 
                              @click="removeSubType(subType.value)" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="flex flex-col gap-2 mt-4">
              <label for="description" class="font-bold">상세 설명</label>
              <Textarea id="description" v-model="formData.description" rows="4" :readonly="props.isViewMode && !isEditReportMode" required />
            </div>

            <div class="flex flex-col gap-2">
              <label for="reporterName" class="font-bold">신고자 이름 <span class="text-red-500 ml-1">*</span></label>
              <InputText id="reporterName" v-model="formData.reporterName" :readonly="props.isViewMode && !isEditReportMode" required />
            </div>

            <div class="flex flex-col gap-2">
              <label for="contact" class="font-bold">연락처 <span class="text-red-500 ml-1">*</span></label>
              <InputText id="contact" v-model="formData.contact" @input="formatContact" maxlength="13" 
                        placeholder="010-0000-0000" :readonly="props.isViewMode && !isEditReportMode" required />
            </div>

            <div class="flex gap-4 mt-6 justify-end">
              <template v-if="props.isViewMode">
                <template v-if="isReporter && !hasProcessingInfo">
                  <Button label="수정" class="p-button" @click="startEditReport" v-if="!isEditReportMode" />
                  <Button label="취소" class="p-button" @click="cancelEditReport" v-if="isEditReportMode" />
                  <Button label="저장" class="p-button" @click="updateReport" v-if="isEditReportMode" />
                </template>
                <Button label="닫기" class="p-button" @click="$emit('close')" v-if="!isEditReportMode" />
                <Button v-if="canDeleteReport && props.isViewMode && !isEditReportMode" label="고장신고 삭제" class="p-button-danger" @click="deleteReport" />
              </template>
              <template v-else>
                <Button label="취소" class="p-button flex-1" @click="handleCancel" />
                <Button label="저장하기" class="flex-1" type="submit" />
              </template>
            </div>
          </form>
        </div>
      </div>
      <!-- 고장신고 조치 내용 -->
      <template v-if="!isNewReportMode">
        <div class="flex justify-between items-center mb-6 pb-4 border-b border-surface-200 dark:border-surface-700">
          <h2 class="m-0 text-2xl">조치 내용</h2>
        </div>
        <div class="flex flex-col gap-4 mt-6">
          <div class="flex flex-col gap-4">
            <!-- 조치 내용 폼 -->
            <div id="issue-action-form" class="bg-surface-100 dark:bg-surface-800 rounded-lg p-4">
              <div class="flex flex-col gap-4">
                <form @submit.prevent="onSubmit" class="flex flex-col gap-4">
                  <!-- 처리 주체 선택 -->
                  <div class="flex flex-col gap-2">
                    <label class="font-bold">처리 주체 <span class="text-red-500 ml-1">*</span></label>
                    <div class="flex flex-wrap gap-4">
                      <div class="flex items-center">
                        <RadioButton
                          inputId="handler-asset"
                          value="ASSET"
                          v-model="newAction.handler_type"
                          name="handler-type"
                          :disabled="localIsViewMode"
                          :class="{ 'error-input': errors.handler_type }"
                        />
                        <label for="handler-asset" class="ml-2 cursor-pointer">자산관리자</label>
                      </div>
                      <div class="flex items-center">
                        <RadioButton
                          inputId="handler-helpdesk"
                          value="HELPDESK"
                          v-model="newAction.handler_type"
                          name="handler-type"
                          :disabled="localIsViewMode"
                          :class="{ 'error-input': errors.handler_type }"
                        />
                        <label for="handler-helpdesk" class="ml-2 cursor-pointer">헬프데스크</label>
                      </div>
                    </div>
                    <span v-if="errors.handler_type" class="error-message">필수 입력 항목입니다</span>
                  </div>

                  <!-- 처리자 입력 -->
                  <div class="flex flex-col gap-2">
                    <label for="new-handler" class="font-bold">처리자 <span class="text-red-500 ml-1">*</span></label>
                    <InputText
                      id="new-handler"
                      v-model="newAction.handler_name"
                      class="w-full"
                      readonly
                      :class="{ 'error-input': errors.handler_name }"
                      ref="handlerInput"
                    />
                    <span v-if="errors.handler_name" class="error-message">필수 입력 항목입니다</span>
                  </div>

                  <!-- 처리일시 입력 -->
                  <div class="flex flex-col gap-2">
                    <label for="new-date" class="font-bold">처리일시 <span class="text-red-500 ml-1">*</span></label>
                    <div class="flex items-center gap-2">
                      <Calendar
                        id="new-date"
                        v-model="newAction.handled_at"
                        :showTime="true"
                        :showSeconds="true"
                        :showIcon="true"
                        dateFormat="yy-mm-dd"
                        timeFormat="HH:mm:ss"
                        hourFormat="24"
                        class="flex-1"
                        :disabled="localIsViewMode"
                        :class="{ 'error-input': errors.handled_at }"
                        ref="dateInput"
                      />
                      <Button
                        v-if="newAction.handled_at && !localIsViewMode"
                        icon="pi pi-eraser"
                        class="p-button p-button-icon-only text-red-500 hover:text-red-700"
                        @click="clearHandledAt"
                        title="처리일시 지우기"
                        style="width: 2rem; height: 2rem; flex-shrink: 0;"
                      />
                    </div>
                    <span v-if="errors.handled_at" class="error-message">필수 입력 항목입니다</span>
                  </div>

                  <!-- 처리상태 선택 -->
                  <div class="flex flex-col gap-2">
                    <label class="font-bold">처리상태 <span class="text-red-500 ml-1">*</span></label>
                    <div class="flex flex-wrap gap-4">
                      <div v-for="(option, idx) in ISSUE_STATUS_OPTIONS" :key="option.value" class="flex items-center">
                        <RadioButton
                          :inputId="'new-status-' + option.value"
                          :value="option.value"
                          v-model="newAction.status"
                          :name="'new-status'"
                          :disabled="localIsViewMode"
                          :class="{ 'error-input': errors.status }"
                          :ref="idx === 0 ? firstStatusRadio : undefined"
                        />
                        <label :for="'new-status-' + option.value" class="ml-2 cursor-pointer">
                          {{ option.name }}
                        </label>
                      </div>
                    </div>
                    <span v-if="errors.status" class="error-message">필수 입력 항목입니다</span>
                  </div>

                  <!-- 처리내용 입력 -->
                  <div class="flex flex-col gap-2">
                    <label for="new-note" class="font-bold">처리내용</label>
                    <Textarea
                      id="new-note"
                      v-model="newAction.note"
                      rows="3"
                      class="w-full"
                      :readonly="localIsViewMode"
                    />
                  </div>

                  <!-- 버튼 영역 -->
                  <div class="flex justify-end gap-2">
                    <Button label="저장" type="submit" v-if="!localIsViewMode" />
                    <Button label="취소" type="button" @click="cancelEdit" v-if="!localIsViewMode" />
                    <Button label="삭제" type="button" @click="deleteAction" v-if="!localIsViewMode" />
                  </div>
                </form>
              </div>
            </div>

            <!-- 편집 버튼 영역 -->
            <div v-if="localIsViewMode && hasEditPermission" class="flex justify-end gap-2">
              <Button 
                label="편집" 
                @click="startEdit"
              />
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, computed, nextTick } from 'vue'
import { ISSUE_STATUS_OPTIONS, ISSUE_STATUS_SEVERITY } from '@/constants/issueStatus';
import fastapi from '@/lib/api';
import axios from 'axios'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Checkbox from 'primevue/checkbox'
import Calendar from 'primevue/calendar'
import RadioButton from 'primevue/radiobutton'
import Toast from 'primevue/toast'
import { useToast } from 'primevue/usetoast'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth';
import QrScanner from './QrScanner.vue'
import Tag from "primevue/tag";

const authStore = useAuthStore();
const isHelpdeskRole = authStore.hasRole('HELPDESK');
const route = useRoute()
const router = useRouter()

const props = defineProps({
  issueId: {
    type: Number,
    required: true
  },
  isViewMode: {
    type: Boolean,
    default: false
  },
  localIsViewMode: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['close', 'refresh'])

const localIsViewMode = ref(props.localIsViewMode)

// 신고 정보 수정 모드 관리
const isEditReportMode = ref(false)
const isReporter = ref(false)
const originalFormData = ref(null)

const assetInfo = ref(null)
const scanning = ref(true)
const camera = ref('environment')
const videoRef = ref(null)
const canvasRef = ref(null)
const stream = ref(null)
let animationFrameId = null
const scannedCode = ref('')
const errorMessage = ref('')

const formData = reactive({
  issueSubType: [],
  description: '',
  reporterName: '',
  contact: ''
})

const expandedGroups = ref(new Set())

const issueTypes = {
  hardware: {
    label: '하드웨어(Hardware) 관련',
    subTypes: [
      { value: 'power', label: '전원이 켜지지 않음' },
      { value: 'boot', label: '부팅 불가 / 부팅 중 멈춤' },
      { value: 'display', label: '모니터 화면 안 나옴 / 화면 깜빡임' },
      { value: 'input', label: '키보드 / 마우스 작동 안 됨' },
      { value: 'usb', label: 'USB 포트 인식 안 됨' },
      { value: 'noise', label: '소음 발생 (팬, 하드디스크 등)' },
      { value: 'heat', label: '발열 과다' },
      { value: 'battery', label: '배터리 충전 안 됨 (노트북의 경우)' }
    ]
  },
  software: {
    label: '소프트웨어(Software) 관련',
    subTypes: [
      { value: 'os_error', label: '운영체제 부팅 오류 (BSOD 포함)' },
      { value: 'program', label: '프로그램 실행 불가' },
      { value: 'crash', label: '응용 프로그램 강제 종료' },
      { value: 'slow', label: '느린 시스템 속도 / 응답 없음' },
      { value: 'virus', label: '바이러스 / 악성코드 의심' },
      { value: 'driver', label: '드라이버 인식 오류' },
      { value: 'function', label: '특정 기능 사용 불가 (예: 프린트, 네트워크)' }
    ]
  },
  network: {
    label: '네트워크(Network) 관련',
    subTypes: [
      { value: 'internet', label: '인터넷 연결 불가' },
      { value: 'intranet', label: '사내 네트워크 미연결' },
      { value: 'vpn', label: 'VPN 접속 오류' },
      { value: 'share', label: '공유 폴더 / NAS 접근 불가' },
      { value: 'ip_conflict', label: 'IP 충돌 의심' }
    ]
  },
  other: {
    label: '기타(Etc.)',
    subTypes: [
      { value: 'login', label: '사용자 계정 로그인 오류' },
      { value: 'permission', label: '권한 부족으로 기능 사용 불가' },
      { value: 'storage', label: '하드디스크 용량 부족' },
      { value: 'security', label: '보안 소프트웨어 충돌' },
      { value: 'custom', label: '기타 (직접 입력)' }
    ]
  }
}

const getSelectedSubTypes = (type) => {
  console.debug(`getSelectedSubTypes 호출됨 - type: ${type}`);
  console.debug(`formData.issueSubType:`, formData.issueSubType);
  
  const selected = issueTypes[type].subTypes.filter(subType => {
    const searchValue = `${type}_${subType.value}`;
    const isIncluded = formData.issueSubType.includes(searchValue);
    console.debug(`검색값: ${searchValue}, 포함여부: ${isIncluded}`);
    return isIncluded;
  });
  
  console.debug(`getSelectedSubTypes(${type}) 결과:`, selected);
  return selected;
}

const selectedTypes = computed(() => {
  const types = Object.keys(issueTypes).filter(type => 
    getSelectedSubTypes(type).length > 0
  )
  console.debug('selectedTypes computed 결과:', types);
  return types;
})

const toast = useToast()
const statusOptions = ISSUE_STATUS_OPTIONS;

// 자산 상태에 따른 severity 결정
const getAssetStatusSeverity = (status) => {
  return ISSUE_STATUS_SEVERITY[status] || 'info';
};

// 자산 상태에 따른 라벨 지정
const getStatusLabel = (status) => {
  const option = statusOptions.find(opt => opt.value === status);
  return option ? option.name : status;
};

// 자산 정보 조회
const handleQRCode = async (qrData) => {
  try {
    if (qrData == null) return
    
    const response = await fastapi(
        'GET',
        `/assets/search/${qrData}`,
        null,
        null,
        null,
        { useToken: true },
    )
    if (response) {
      assetInfo.value = response;
    }
  } catch (error) {
    console.error(`자산 정보 조회 실패: ${qrData}`, error)
    if (error.response) {
      switch (error.response.status) {
        case 401:
          errorMessage.value = '로그인이 필요합니다. 다시 로그인해주세요.'
          break
        case 404:
          errorMessage.value = '해당 자산을 찾을 수 없습니다.'
          break
        default:
          errorMessage.value = '자산 정보를 불러오는데 실패했습니다.'
      }
    } else {
      errorMessage.value = '서버 연결에 실패했습니다.'
    }
    assetInfo.value = null
  } finally {
    scanning.value = false
  }
}

onMounted(async () => {
  if (props.isViewMode && props.issueId) {
    await fetchIssueReport(props.issueId)
  } else if (!props.isViewMode) {
    // 약간의 지연을 주어 DOM이 완전히 로드된 후 카메라 초기화
    await new Promise(resolve => setTimeout(resolve, 300));
  }
  window.addEventListener('keydown', handleKeyDown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown)
})

const handleCancel = () => {
  if (isEditReportMode.value) {
    if (confirm('수정한 내용이 저장되지 않습니다. 정말 취소하시겠습니까?')) {
      cancelEditReport();
    }
  } else if (confirm('입력한 내용이 저장되지 않습니다. 정말 닫으시겠습니까?')) {
    route.path.includes('/IssueReportDialog') ? router.back() : emit('close')
  }
}

const handleKeyDown = (event) => {
  if (event.key === 'Escape') {
    if (isEditReportMode.value) {
      handleCancel()
    } else if (!props.isViewMode) {
      handleCancel()
    } else {
      route.path.includes('/IssueReportDialog') ? router.back() : emit('close')
    }
  }
}

// 고장신고 접수
const submitReport = async () => {
  try {
    // 필수항목 체크
    if (formData.issueSubType.length === 0) {
      alert('고장 유형을 하나 이상 선택해주세요.')
      return
    }

    if (!formData.reporterName.trim()) {
      alert('신고자 이름을 입력해주세요.')
      return
    }

    if (!formData.contact.trim()) {
      alert('연락처를 입력해주세요.')
      return
    }

    if (!assetInfo.value?.id) {
      alert('자산 정보를 찾을 수 없습니다. QR 코드를 다시 스캔해주세요.')
      return
    }

    // issueSubType을 issueType으로 변환
    const issueType = formData.issueSubType.map(subType => {
      const [type, subTypeValue] = subType.split('_')
      return {
        type,
        sub_type: subTypeValue
      }
    })

    const reportData = {
      assetId: Number(assetInfo.value.id),
      assetNumber: assetInfo.value.assetNumber,
      issueType: JSON.stringify(issueType),
      description: formData.description,
      reporterName: formData.reporterName,
      contact: formData.contact
    }

    console.debug('제출할 데이터:', reportData);

    await fastapi(
      'POST',
      '/issue/report',
      reportData,
      null,
      null,
      { useToken: true }
    )

    alert('고장신고가 접수되었습니다.')
    emit('refresh', reportData)
    route.path.includes('/IssueReportDialog') ? router.back() : emit('close')
  } catch (error) {
    console.error('고장신고 접수 실패:', error)
    if (error.response?.data) {
      console.error('에러 상세:', error.response.data)
      alert(`고장신고 접수에 실패했습니다: ${error.response.data.detail || '알 수 없는 오류'}`)
    } else {
      alert('고장신고 접수에 실패했습니다.')
    }
  }
}

const toggleGroup = (type) => {
  if (expandedGroups.value.has(type)) {
    expandedGroups.value.delete(type)
  } else {
    expandedGroups.value.add(type)
  }
}

const removeSubType = (subTypeValue) => {
  const index = formData.issueSubType.indexOf(subTypeValue)
  if (index !== -1) {
    formData.issueSubType.splice(index, 1)
  }
}

// 연락처 포맷팅
const formatContact = (event) => {
  let value = event.target.value.replace(/[^0-9]/g, '')
  
  if (value.length > 11) {
    value = value.slice(0, 11)
  }
  
  if (value.length > 0) {
    if (value.length <= 3) {
      value = value
    } else if (value.length <= 7) {
      value = value.slice(0, 3) + '-' + value.slice(3)
    } else {
      value = value.slice(0, 3) + '-' + value.slice(3, 7) + '-' + value.slice(7)
    }
  }
  
  formData.contact = value
}

// 고장신고 조회
const fetchIssueReport = async (issueId) => {
  try {
    const response = await fastapi(
      'GET',
      `/issue/report/${issueId}`,
      null,
      null,
      null,
      { useToken: true }
    )
    
    if (response) {
      // 신고자 본인 여부 확인
      isReporter.value = response.reporterName === authStore.user?.name;
      
      // 자산 정보 설정
      assetInfo.value = {
        ...response,
        status: response.status
      };
      
      // 폼 데이터 설정
      formData.description = response.description
      formData.reporterName = response.reporterName
      formData.contact = response.contact
      
      // 처리일시 설정 (issue_report의 handled_at 사용)
      if (response.handled_at) {
        newAction.value.handled_at = new Date(response.handled_at)
      }

      // 처리 주체 설정
      newAction.value.handler_type = response.handler_type || 'ASSET'
      console.debug('처리 주체 설정:', response.handler_type)
      
      // 처리 이력 데이터 가져오기
      const actionsResponse = await fastapi(
        'GET',
        `/issue/action/${issueId}`,
        null,
        null,
        null,
        { useToken: true }
      )
      
      if (actionsResponse) {
        issueActions.value = actionsResponse
        if (actionsResponse.length > 0) {
          // 기존 처리 정보가 있는 경우 해당 정보 사용
          const lastAction = actionsResponse[0];
          newAction.value = { 
            ...lastAction, 
            status: lastAction.status || 'REGISTERED',  // 상태값이 없으면 기본값 설정
            handler_type: response.handler_type || 'ASSET'  // 처리 주체 설정
          }
          console.debug('처리 내역에서 처리 주체 설정:', response.handler_type)
        } else {
          // 처리 내용이 없는 경우 빈 값으로 초기화
          newAction.value = {
            handler_name: '',
            handled_at: null,
            status: 'REGISTERED',  // 기본값을 영문 enum으로 설정
            handler_type: response.handler_type || 'ASSET',  // 처리 주체 설정
            note: ''
          }
        }
      }

      // issueType 데이터 설정
      console.debug('서버 응답 전체:', response);
      console.debug('response.issueType 원본:', response.issueType);
      console.debug('response.issueType 타입:', typeof response.issueType);
      
      let issueType = null;
      try {
        if (response.issueType) {
          // 첫 번째 파싱: JSON 문자열을 객체로 변환
          let parsed = JSON.parse(response.issueType);
          console.debug('첫 번째 파싱 결과:', parsed);
          
          // 두 번째 파싱: 만약 여전히 문자열이라면 다시 파싱
          if (typeof parsed === 'string') {
            issueType = JSON.parse(parsed);
            console.debug('두 번째 파싱 결과:', issueType);
          } else {
            issueType = parsed;
          }
          
          console.debug('최종 파싱된 issueType:', issueType);
        } else {
          console.debug('response.issueType이 null 또는 undefined');
        }
      } catch (parseError) {
        console.error('issueType JSON 파싱 오류:', parseError);
        console.debug('파싱 실패한 원본 데이터:', response.issueType);
      }
      
      if (issueType && Array.isArray(issueType)) {
        formData.issueSubType = issueType.map(type => {
          console.debug('처리 중인 type 객체:', type);
          
          // sub_type과 subType 두 가지 필드명 모두 처리
          const subTypeValue = type.sub_type || type.subType;
          
          if (type.type && subTypeValue) {
            // 특별한 매핑 처리
            let mappedSubType = subTypeValue;
            if (type.type === 'software' && subTypeValue === 'os') {
              mappedSubType = 'os_error';
            }
            
            const result = `${type.type}_${mappedSubType}`;
            console.debug(`원본: ${type.type}_${subTypeValue}, 매핑된 키: ${result}`);
            return result;
          }
          console.debug('type 또는 sub_type/subType이 없음:', type);
          return null;
        }).filter(Boolean)
        
        console.debug('변환된 formData.issueSubType:', formData.issueSubType);
        console.debug('formData.issueSubType.length:', formData.issueSubType.length);
        
        // Vue의 반응성 시스템이 업데이트된 후 그룹을 펼치기
        await nextTick();
        
        // 선택된 고장 유형 그룹들을 자동으로 펼치기 (편집 모드에서만)
        if (!props.isViewMode || isEditReportMode) {
          issueType.forEach(type => {
            if (type.type) {
              expandedGroups.value.add(type.type)
            }
          })
        }
        
        // 편집 모드에서 체크박스 상태 설정을 위한 추가 nextTick
        if (!props.isViewMode || isEditReportMode) {
          await nextTick();
        }
        
        console.debug('펼쳐진 그룹들:', expandedGroups.value);
        console.debug('selectedTypes computed:', selectedTypes.value);
        console.debug('props.isViewMode:', props.isViewMode);
        console.debug('isEditReportMode.value:', isEditReportMode.value);
      } else {
        console.debug('issueType이 null이거나 배열이 아님:', issueType);
        console.debug('issueType 타입:', typeof issueType);
        if (issueType) {
          console.debug('issueType 내용:', issueType);
        }
      }
    }
  } catch (error) {
    console.error('고장신고 조회 실패:', error)
    alert('고장신고 정보를 불러오는데 실패했습니다.')
  }
}
// 처리 내용 업데이트
const updateAction = async (action) => {
  try {
    console.debug('updateAction 호출됨', action);
    
    if (!action.id) {
      console.debug('Action ID 미존재:', action.id);
      toast.add({ 
        severity: 'error', 
        summary: '오류', 
        detail: '처리 정보 ID가 없습니다.', 
        life: 5000 
      });
      return;
    }

    const response = await fastapi(
      'PUT',
      `/issue/action/${action.id}`,
      { 
        id: action.id,
        issue_id: action.issue_id,
        note: action.note,
        handler_name: action.handler_name,
        handled_at: formatDateTimeForServer(action.handled_at),
        status: action.status,
        handler_type: action.handler_type  // 처리 주체 추가
      },
      null,
      null,
      { useToken: true }
    )
    
    if (response) {
      // 기존 action을 업데이트된 내용으로 교체
      const index = issueActions.value.findIndex(a => a.id === action.id)
      if (index !== -1) {
        issueActions.value[index] = response
      }
      toast.add({ severity: 'success', summary: '성공', detail: '처리 내용이 업데이트되었습니다.', life: 3000 })
      // 유효성 오류 UX 표시 원복
      errors.handler_name = false;
      errors.handled_at = false;
      errors.status = false;
      errors.handler_type = false;
      localIsViewMode.value = true
    }
  } catch (error) {
    console.error('처리 내용 업데이트 실패:', error)
    toast.add({ severity: 'error', summary: '오류', detail: '처리 내용 업데이트에 실패했습니다.', life: 3000 })
  }
}

// issueActions ref 추가
const issueActions = ref([])

const newAction = ref({
  handler_name: '',
  handled_at: null,
  status: '',  // 기본값 제거 - 백엔드에서 처리
  handler_type: '',  // 기본값 제거 - 백엔드에서 처리
  note: ''
})

const handlerInput = ref(null)
const dateInput = ref(null)
const firstStatusRadio = ref(null)
const errors = reactive({
  handler_name: false,
  handled_at: false,
  status: false,
  handler_type: false  // 에러 상태 추가
})

const onSubmit = () => {
  errors.handler_name = !newAction.value.handler_name
  errors.handled_at = !newAction.value.handled_at
  errors.status = !newAction.value.status
  errors.handler_type = !newAction.value.handler_type  // 유효성 검사 추가

  if (errors.handler_name) {
    handlerInput.value?.$el?.focus?.()
    toast.add({ severity: 'error', summary: '필수 입력', detail: '처리자를 입력하세요.', life: 3000 })
    return
  }
  if (errors.handled_at) {
    dateInput.value?.$el?.focus?.()
    toast.add({ severity: 'error', summary: '필수 입력', detail: '처리일시를 선택하세요.', life: 3000 })
    return
  }
  if (errors.status) {
    firstStatusRadio.value?.$el?.focus?.()
    toast.add({ severity: 'error', summary: '필수 입력', detail: '처리상태를 선택하세요.', life: 3000 })
    return
  }
  if (errors.handler_type) {
    toast.add({ severity: 'error', summary: '필수 입력', detail: '처리 주체를 선택하세요.', life: 3000 })
    return
  }
  addNewAction()
}

// 새 처리 추가
const addNewAction = async () => {
  try {
    console.debug('addNewAction 호출됨', newAction.value);
    
    // 기존 action이 있는지 확인
    const existingAction = issueActions.value[0]
    let response

    if (existingAction) {
      // 기존 action이 있으면 updateAction 함수 사용 (ID 포함)
      const actionWithId = {
        ...newAction.value,
        id: existingAction.id
      };
      await updateAction(actionWithId)
      return
    } else {
      // 새로운 action이면 POST로 생성
      response = await fastapi(
        'POST',
        `/issue/action/${props.issueId}`,
        {
          ...newAction.value,
          handled_at: formatDateTimeForServer(newAction.value.handled_at)
        },
        null,
        null,
        { useToken: true }
      )
    }
    
    if (response) {
      // 기존 action이 있으면 업데이트, 없으면 새로 추가
      const index = issueActions.value.findIndex(a => a.id === response.id)
      if (index !== -1) {
        issueActions.value[index] = response
      } else {
        issueActions.value.push(response)
      }
      
      toast.add({ severity: 'success', summary: '성공', detail: '처리 내용이 등록되었습니다.', life: 3000 })
      // 유효성 오류 UX 표시 원복
      errors.handler_name = false;
      errors.handled_at = false;
      errors.status = false;
      errors.handler_type = false;
      localIsViewMode.value = true
    }
  } catch (error) {
    console.error('새 처리 추가 실패:', error)
    toast.add({ severity: 'error', summary: '오류', detail: '처리 내용 등록에 실패했습니다.', life: 3000 })
  }
}
const deleteAction = async () => {
  try {
    // 기존 action이 있는지 확인
    const existingAction = issueActions.value[0]
    let response

    if (existingAction) {
      // 기존 action이 있으면 DELETE로 삭제
      response = await fastapi(
        'DELETE',
        `/issue/action/${existingAction.id}`,
        null,
        null,
        null,
        { useToken: true }
      )
    } else {
      return
    }
    
    if (response) {
      // 처리정보 초기화
      issueActions.value = []
      newAction.value = {
        handler_name: '',
        handled_at: null,
        status: '',
        note: ''
      }
      
      toast.add({ severity: 'success', summary: '성공', detail: '처리 내용이 삭제되었습니다.', life: 3000 })
      localIsViewMode.value = true
    }
  } catch (error) {
    console.error('처리 내용 삭제 실패:', error)
    toast.add({ severity: 'error', summary: '오류', detail: '처리 내용 삭제에 실패했습니다.', life: 3000 })
  }
}

// 편집 시작 함수
const startEdit = () => {
  console.debug('startEdit 호출됨, 현재 상태:', {
    handler_name: newAction.value.handler_name,
    handled_at: newAction.value.handled_at,
    status: newAction.value.status,
    handler_type: newAction.value.handler_type
  });
  
  // 권한 체크
  if (!canEdit.value) {
    toast.add({ 
      severity: 'error', 
      summary: '권한 없음', 
      detail: '해당 처리 내용을 편집할 권한이 없습니다.', 
      life: 3000 
    });
    return;
  }
  
  // 기존 처리 정보가 없는 경우에만 기본값 설정
  if (!newAction.value.handler_name) {
    newAction.value.handler_name = authStore.user?.name || '';
    console.debug('처리자 기본값 설정:', newAction.value.handler_name);
  }
  if (!newAction.value.handled_at) {
    newAction.value.handled_at = new Date();
    console.debug('처리일시 기본값 설정:', newAction.value.handled_at);
  }
  
  console.debug('편집 시작 후 상태:', newAction.value);
  
  // 편집 시작 시 유효성 오류 UX 표시 초기화
  errors.handler_name = false;
  errors.handled_at = false;
  errors.status = false;
  errors.handler_type = false;
  
  localIsViewMode.value = false;
}

// 편집 취소 함수
const cancelEdit = () => {
  // 기존 처리 정보가 있는지 확인
  const existingAction = issueActions.value[0];
  
  if (existingAction) {
    // 기존 처리 정보가 있으면 원래 값으로 복원
    newAction.value = {
      ...existingAction,
      status: existingAction.status || ''
    };
  } else {
    // 기존 처리 정보가 없으면 초기화
    newAction.value = {
      handler_name: '',
      handled_at: null,
      status: '',
      note: ''
    };
  }
  
  // 유효성 오류 UX 표시 원복
  errors.handler_name = false;
  errors.handled_at = false;
  errors.status = false;
  
  localIsViewMode.value = true;
}

// 처리일시 지우기 함수
const clearHandledAt = () => {
  newAction.value.handled_at = null;
}

const isNewReportMode = computed(() => {
  return !props.isViewMode && (!props.issueId || props.issueId === 0);
});

// 처리 정보가 있는지 확인
const hasProcessingInfo = computed(() => {
  return issueActions.value.length > 0 && issueActions.value[0].handler_name;
});

// 편집 권한 체크 computed 속성 수정
const canEdit = computed(() => {
  const userRoles = authStore.role?.split(',') || [];
  // ADMIN 또는 ASSET 권한이 있으면 무조건 true
  if (userRoles.some(role => ['ADMIN', 'ADMIN_A', 'ASSET'].includes(role))) return true;
  // HELPDESK 권한이 있고 처리 주체가 HELPDESK인 경우
  if (userRoles.includes('HELPDESK') && newAction.value.handler_type === 'HELPDESK') {
    return true;
  }
  return false;
});

// 편집 권한 체크 computed 속성 수정
const canDeleteReport = computed(() => {
  // 관리자 권한(여러 역할 중 하나라도 있으면 true)
  if ([
    'ADMIN', 'ADMIN_A', 'ASSET', 'ASSET_PC', 'ASSET_ADMIN'
  ].some(r => authStore.hasRole(r))) return true;
  // 신고자 본인 & 처리 이력 없음
  if (isReporter.value && !hasProcessingInfo.value) return true;
  return false;
});

const hasEditPermission = computed(() => {
  const userRoles = (authStore.role?.split(',') || []).map(r => r.trim());
  // ADMIN, ADMIN_A, ASSET, ASSET_PC, ASSET_ADMIN 권한이 있으면 true
  if ([
    'ADMIN', 'ADMIN_A', 'ASSET', 'ASSET_PC', 'ASSET_ADMIN'
  ].some(r => userRoles.includes(r))) return true;
  // HELPDESK 권한이 있고 처리 주체가 HELPDESK인 경우
  if (userRoles.includes('HELPDESK') && newAction.value.handler_type === 'HELPDESK') {
    return true;
  }
  return false;
});

const deleteReport = async () => {
  if (!props.issueId) return;
  if (!confirm('정말로 이 고장신고를 삭제하시겠습니까? 삭제 후 복구할 수 없습니다.')) return;
  try {
    await fastapi(
      'DELETE',
      `/issue/report/${props.issueId}`,
      null,
      null,
      null,
      { useToken: true }
    );
    toast.add({ severity: 'success', summary: '삭제 완료', detail: '고장신고가 삭제되었습니다.', life: 3000 });
    emit('refresh');
    route.path.includes('/IssueReportDialog') ? router.back() : emit('close');
  } catch (error) {
    let msg = '고장신고 삭제에 실패했습니다.';
    if (error.response?.data?.detail) msg = error.response.data.detail;
    toast.add({ severity: 'error', summary: '오류', detail: msg, life: 4000 });
  }
};

function formatDateTime(dt) {
  if (!dt) return '';
  const date = new Date(dt);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
}

// 서버로 전송할 때 로컬 시간대를 유지하는 함수
function formatDateTimeForServer(dt) {
  if (!dt) return null;
  const date = new Date(dt);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}`;
}

// 신고 정보 수정 시작
const startEditReport = () => {
  // 현재 폼 데이터를 백업
  originalFormData.value = {
    issueSubType: [...formData.issueSubType],
    description: formData.description,
    reporterName: formData.reporterName,
    contact: formData.contact
  };
  isEditReportMode.value = true;
  
  // 편집 모드에서 선택된 고장 유형 그룹들을 펼치기
  nextTick(() => {
    formData.issueSubType.forEach(subType => {
      const [type] = subType.split('_');
      if (type) {
        expandedGroups.value.add(type);
      }
    });
  });
}

// 신고 정보 수정 취소
const cancelEditReport = () => {
  // 원래 데이터로 복원
  if (originalFormData.value) {
    formData.issueSubType = [...originalFormData.value.issueSubType];
    formData.description = originalFormData.value.description;
    formData.reporterName = originalFormData.value.reporterName;
    formData.contact = originalFormData.value.contact;
  }
  isEditReportMode.value = false;
  originalFormData.value = null;
}

// 신고 정보 업데이트
const updateReport = async () => {
  try {
    // 필수항목 체크
    if (formData.issueSubType.length === 0) {
      toast.add({ severity: 'error', summary: '오류', detail: '고장 유형을 하나 이상 선택해주세요.', life: 3000 });
      return;
    }

    if (!formData.reporterName.trim()) {
      toast.add({ severity: 'error', summary: '오류', detail: '신고자 이름을 입력해주세요.', life: 3000 });
      return;
    }

    if (!formData.contact.trim()) {
      toast.add({ severity: 'error', summary: '오류', detail: '연락처를 입력해주세요.', life: 3000 });
      return;
    }

    // issueSubType을 issueType으로 변환
    const issueType = formData.issueSubType.map(subType => {
      const [type, subTypeValue] = subType.split('_')
      return {
        type,
        sub_type: subTypeValue
      }
    })

    const updateData = {
      issueType: JSON.stringify(issueType),
      description: formData.description,
      reporterName: formData.reporterName,
      contact: formData.contact
    }

    console.debug('업데이트할 데이터:', updateData);

    await fastapi(
      'PUT',
      `/issue/report/${props.issueId}`,
      updateData,
      null,
      null,
      { useToken: true }
    )

    toast.add({ severity: 'success', summary: '성공', detail: '고장신고 정보가 수정되었습니다.', life: 3000 });
    isEditReportMode.value = false;
    originalFormData.value = null;
    emit('refresh');
  } catch (error) {
    console.error('고장신고 수정 실패:', error);
    if (error.response?.data) {
      console.error('에러 상세:', error.response.data);
      toast.add({ severity: 'error', summary: '오류', detail: `고장신고 수정에 실패했습니다: ${error.response.data.detail || '알 수 없는 오류'}`, life: 5000 });
    } else {
      toast.add({ severity: 'error', summary: '오류', detail: '고장신고 수정에 실패했습니다.', life: 3000 });
    }
  }
}
</script>

<style scoped>
:deep(.p-datatable .p-datatable-thead > tr > th) {
  @apply bg-surface-100 dark:bg-surface-800 font-semibold;
  @apply text-surface-700 dark:text-surface-0;
}

:deep(.p-datatable .p-datatable-tbody > tr > td) {
  @apply text-surface-700 dark:text-surface-0;
  @apply border-surface-200 dark:border-surface-700;
}

:deep(.p-datatable .p-datatable-tbody > tr:hover) {
  @apply bg-surface-100 dark:bg-surface-800/60;
}

:deep(.p-dropdown .p-inputtext),
:deep(.p-DatePicker .p-inputtext) {
  @apply text-sm;
}

.p-button.p-button-icon-only.close-btn {
  width: 2.5rem;
  height: 2.5rem;
}

.p-button.p-button-icon-only.close-btn:hover {
  opacity: 1;
}

.camera-btn {
  flex: 1;
  max-width: 200px;
}

@media (max-width: 768px) {
  .camera-btn {
    padding: 0.625rem 0.75rem;
    font-size: 0.85rem;
  }
}
.error-input {
  border: 1.5px solid #f44336 !important;
  box-shadow: 0 0 0 1px #f44336 !important;
}
.error-message {
  color: #f44336;
  font-size: 0.95em;
  margin-top: 2px;
}
</style> 
